/**
 * The `Chat` card: the last message preview with its unread count, or the
 * off variant the Commissioner's switch produces (Q87/MSG-10: nothing is
 * ever deleted by turning chat off, so the card stays visible either way).
 * The read payload deliberately matches the shell's own badge query so the
 * two share one cached response instead of issuing a second request.
 *
 * THE CARD OPENS THE CHAT. It used to open nothing, on the grounds that the
 * pool chat screen was a later card's scope. That screen shipped: it is
 * `ConversationPane`, mounted by `/messages` under `?conversationId=`, and
 * this card sat in front of it showing a preview with no way through. There
 * was no other path either, so a fan on the web could not reach a pool chat
 * at all. `onOpenChat` is that path.
 *
 * The button is absent, not disabled, while there is no chat id to open: the
 * list read is still in flight, or this pool's chat has never been
 * provisioned. A disabled control would be a promise the card cannot keep,
 * and the card still reads correctly as a preview without it.
 *
 * THE PENDING STATE. A pool whose chat is on but which has no chat in the
 * fan's list is a pool whose chat is still being set up: pool-api enqueues
 * the provisioning off the same `/pool/get` this screen just made
 * (services/pool-api/src/lib/healPoolChatOnRead.ts), and the worker delivers
 * it in seconds. The card says so ("Setting up the chat") instead of "No
 * messages yet", which described a chat that did not exist as if it were
 * merely empty. While pending, the list is re-read every
 * CHAT_PENDING_POLL_MS (chatListPollIntervalMs in poolHomeState.ts) so the
 * card turns into the real preview with its Open chat button without the fan
 * reloading. The poll stops the moment the chat appears, the chat is switched
 * off, or the card unmounts, so a healthy pool never pays for it.
 */
import { useChatListMine } from "@sportsmet/messaging-sdk/private";
import { Button, Card } from "@sportsmet/ui";
import type { ReactNode } from "react";

import {
  CHAT_CARD_TITLE,
  CHAT_OFF_BADGE,
  CHAT_OFF_BODY,
  CHAT_OFF_NOTE,
  CHAT_OPEN_LABEL,
  CHAT_PENDING_BODY,
  CHAT_PENDING_NOTE,
  chatListPollIntervalMs,
  findPoolChat,
  resolveChatCard,
} from "./poolHomeState";

export type typePoolHomeChatCardProps = {
  poolId: string;
  chatEnabled: boolean;
  onOpenChat: (chatId: string) => void;
};

export const PoolHomeChatCard = ({
  poolId,
  chatEnabled,
  onOpenChat,
}: typePoolHomeChatCardProps): ReactNode => {
  const chats = useChatListMine(
    {},
    {
      // Decided per observer from the cached answer, so the poll is on only
      // while this pool's chat is genuinely missing from a loaded list.
      refetchInterval: (query) =>
        chatListPollIntervalMs({
          chatEnabled,
          poolId,
          items: query.state.data?.data?.items ?? null,
        }),
    },
  );

  // An error envelope answers `data: null`, which is not a loaded list either:
  // a fan whose chat list failed to load is not told the chat is being set up.
  const items = chats.data?.data?.items ?? null;
  const model = resolveChatCard({
    chatEnabled,
    chat: findPoolChat({ poolId, items }),
    isChatListLoaded: items !== null,
  });

  if (model.variant === "pending") {
    return (
      <Card title={CHAT_CARD_TITLE} data-testid="pool-home-chat">
        <p className="text-body-strong text-ink">{CHAT_PENDING_BODY}</p>
        <p className="text-caption text-ink-secondary">{CHAT_PENDING_NOTE}</p>
      </Card>
    );
  }

  const chatId = model.chatId;
  const openButton =
    chatId === null ? null : (
      <Button
        variant="link"
        size="md"
        data-testid="pool-home-chat-open"
        onClick={() => {
          onOpenChat(chatId);
        }}
      >
        {CHAT_OPEN_LABEL}
      </Button>
    );

  if (model.variant === "off") {
    return (
      <Card
        title={CHAT_CARD_TITLE}
        data-testid="pool-home-chat"
        badge={
          <span className="text-caption text-ink-secondary">
            {CHAT_OFF_BADGE}
          </span>
        }
      >
        <p className="text-body-strong text-ink">{CHAT_OFF_BODY}</p>
        <p className="text-caption text-ink-secondary">{CHAT_OFF_NOTE}</p>
        {openButton}
      </Card>
    );
  }

  return (
    <Card
      title={CHAT_CARD_TITLE}
      data-testid="pool-home-chat"
      badge={
        model.unreadCount > 0 ? (
          <span className="grid min-w-6 place-items-center rounded-full bg-primary px-2 text-caption font-bold text-ink-inverse">
            {model.unreadCount}
          </span>
        ) : undefined
      }
    >
      <p className="truncate text-body text-ink">{model.preview}</p>
      {openButton}
    </Card>
  );
};
